import React, {useState} from 'react';

import {
    AttributeDescriptionGenerationView,
} from 'src/modules/ai/attribute-description-generation/attribute-description-generation.view';
import {TourAnchor} from 'src/modules/ai/tour/tour-anchor';
import {createTypingEffect} from 'src/modules/ai/utils/createTypingEffect';
import {
    serializeAttributeTypeFields,
} from 'src/modules/ai/utils/serializeAttributeTypeFields';
import {generateAttributeDescription} from 'src/openapi/apis/AttributeDescriptionGeneratorControllerApi';
import {EventChangeRequest} from 'src/openapi/models/EventChangeRequest';
import {FieldType} from 'src/openapi/models/FieldType';
import {useAppDispatch} from 'src/store';

import {Icons, Sizes} from '@pb/kit-component-icon';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';

type Props = {
    attributeName: string | undefined,
    attributeType: string | undefined,
    eventChangeRequest: EventChangeRequest | undefined,
    onDescriptionGenerate: (description: string) => void,
};

const GENERATE_DESCRIPTION_ERROR_MESSAGE = 'Failed to generate attribute description. Please try again.';
const GENERATE_DESCRIPTION_TYPE_ERROR_MESSAGE = 'Please select "Data Type"';
const ERROR_AUTO_HIDE_TIMEOUT = 10000;

export const AttributeDescriptionGenerationController = (props: Props) => {
    const {
        attributeName,
        attributeType,
        eventChangeRequest,
        onDescriptionGenerate,
    } = props;

    const dispatch = useAppDispatch();
    const {onCreateNote} = usePushNotification();
    const [onGenerating, setOnGenerating] = useState(false);
    const [description, setDescription] = useState('');

    const onGenerateClick = async () => {
        if (!attributeType) {
            await onCreateNote({
                id: 'generateAttributeDescriptionMissingType',
                status: Status.ERROR,
                message: GENERATE_DESCRIPTION_TYPE_ERROR_MESSAGE,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
            });
            return;
        }

        if (attributeName && eventChangeRequest) {
            setOnGenerating(true);

            const request = {
                name: attributeName,
                generateAttributeDescriptionRequest: {
                    ...serializeAttributeTypeFields(eventChangeRequest),
                    openApiDiscriminator: 'GenerateAttributeDescriptionRequest',
                    attributeType: attributeType as FieldType,
                },
            };

            const response = await dispatch(generateAttributeDescription(request));

            if (response.payload.success) {
                createTypingEffect(response.payload.data, setDescription);
            }

            if (response.payload.error) {
                await onCreateNote({
                    id: 'generateAttributeDescription',
                    status: Status.ERROR,
                    message: GENERATE_DESCRIPTION_ERROR_MESSAGE,
                    buttonProps: {iconProps: {type: Icons.CLOSE}},
                    autoHideTimeout: 10000,
                });
            }

            setOnGenerating(false);
        }
    };

    return (
        <>
            {attributeName ? (
                <AttributeDescriptionGenerationView
                    attributeName={attributeName}
                    description={description}
                    isDisabled={!eventChangeRequest}
                    setDescription={setDescription}
                    onGenerateClick={onGenerateClick}
                    onGenerating={onGenerating}
                    onDescriptionGenerate={onDescriptionGenerate}
                />
            ) : (
                <TourAnchor
                    className="attribute-description-generation-tour"
                    icon={Icons.SPARKLES}
                    iconSize={Sizes.Size_16}
                />
            )}
        </>
    );
};
